<template>
  <div class="terms">
    <div class="header mt-2">
      <div />
      <div class="h1 flex justify-content-center">
        Privacy Policy
      </div>
      <div class="flex justify-content-end">
        <AuButton
          center
          width="100px"
          @click="openDoc"
        >
          Download
        </AuButton>
      </div>
    </div>
    <div class="h5 mt-5 ms-4 me-4">
      <p><strong>Version:</strong> 3.0</p>
      <p><strong>Effective Date:</strong> [●]</p>
      <p><strong>Data Controller:</strong> [Legal Entity Name], trading as "RWA.capital" ("Company", "we", "us")</p>
      <p><strong>Registered address:</strong> [●]</p>
      <p><strong>Contact:</strong> <a href="mailto:privacy@rwa.capital">privacy@rwa.capital</a></p>

      <h3 class="mt-4">
        1. INTRODUCTION
      </h3>
      <p>
        This Privacy Policy explains how we collect, use, store, disclose and
        protect personal data when offering access to our website, mobile
        application and related services (the Platform), including when you
        create an account, complete identity checks, invest through the Platform
        (including into SPVs or similar investment vehicles), or subscribe to
        updates.
      </p>
      <p>
        Should you not agree with this Policy, please do not use the Platform.
      </p>

      <h3 class="mt-4">
        2. DATA WE COLLECT
      </h3>

      <h4 class="mt-3">
        2.1 Data you submit
      </h4>
      <ul>
        <li><strong>Identity & verification details:</strong> name, date of birth, nationality, passport/ID details, selfie/liveness detection images, as well as any other KYC data.</li>
        <li><strong>Contact details:</strong> email, phone number, residence address.</li>
        <li><strong>Investor & financial details:</strong> banking data, source of funds/wealth, income range, investment objectives, risk profile and transaction history.</li>
        <li><strong>Communication details:</strong> support requests, call/chat/email records and feedback you provide.</li>
      </ul>

      <h4 class="mt-3">
        2.2 Data collected automatically
      </h4>
      <ul>
        <li><strong>Technology & device details:</strong> IP address, device identifiers, browser/app version, OS, time zone, crash logs.</li>
        <li><strong>Usage details:</strong> pages/screens viewed, features used, records of clicks, referral URLs, and performance metrics.</li>
        <li><strong>Cookies & similar tools:</strong> online identifiers and preferences. For details and settings, please see our Cookie Policy (where applicable).</li>
      </ul>

      <h4 class="mt-3">
        2.3 Data from third parties
      </h4>
      <p>
        We may receive data from (i) identity/KYC providers, (ii) payment
        providers and banks, (iii) analytics and communications providers, (iv)
        sanctions, PEP and adverse media screening databases, and (v) public
        registries, where permitted by law.
      </p>

      <h3 class="mt-4">
        3. HOW WE USE PERSONAL DATA
      </h3>
      <p>
        We process personal data only in case we have a respective lawful basis,
        such as performing within a respective contract we have entered with
        you, complying with legal/regulatory obligations, our legitimate
        interests (e.g., security and service improvement), or your consent
        (e.g., certain marketing distribution and cookies).
      </p>
      <p><strong>Typical processing purposes include:</strong></p>
      <ul>
        <li><strong>Service delivery:</strong> onboarding, account administration, processing of investments and distributions, and operating the Platform.</li>
        <li><strong>Regulatory compliance (KYC/AML/CTF):</strong> identity verification, sanctions screening, suitability/appropriateness checks (if required), fraud prevention and reporting to regulators.</li>
        <li><strong>Support & communications:</strong> responding to requests, notifications regarding changes to our terms/policies, and sending service messages.</li>
        <li><strong>Platform improvement & security:</strong> analytics, testing, troubleshooting, monitoring for misuse, as well as maintaining safety and reliability of infrastructure.</li>
        <li><strong>Marketing:</strong> sending updates about investment opportunities and products, where permitted and subject to opt-out.</li>
      </ul>
      <p>
        We do not use solely automated decision-making that entails legal or
        similarly significant effects without appropriate safeguards; where such
        processing is required (e.g., for fraud screening), we will provide duly
        required notices.
      </p>

      <h3 class="mt-4">
        4. DISCLOSURE OF PERSONAL DATA
      </h3>
      <p>
        We are within our right to share personal data on a need-to-know basis
        with:
      </p>
      <ul>
        <li><strong>Service providers:</strong> IT hosting, cloud services, KYC/ID verification, communications, analytics and payment processing.</li>
        <li><strong>Transaction counterparties:</strong> SPVs, trustees/nominees, property managers, registrars and other parties as may be needed for investment execution and management.</li>
        <li><strong>Professional advisers:</strong> lawyers, auditors, insurers and banks.</li>
        <li><strong>Regulators & authorities:</strong> competent UAE authorities and regulators (and overseas authorities where required) for compliance, reporting and legally valid requests.</li>
        <li><strong>Business transfers:</strong> buyers/investors in connection with mergers, acquisitions or asset sale.</li>
      </ul>
      <p>
        We do not sell personal data to advertisers.
      </p>

      <h3 class="mt-4">
        5. INTERNATIONAL DATA TRANSFER
      </h3>
      <p>
        We are based in the UAE, yet certain recipients may be located outside
        the UAE. Where we transfer personal data internationally, we apply
        appropriate safeguards (e.g., contractual protections and vendor due
        diligence), as well as we transfer only data that is deemed required for
        the duly stated purposes.
      </p>

      <h3 class="mt-4">
        6. DATA SECURITY
      </h3>
      <p>
        We employ administrative, technical and physical safeguards designed to
        protect personal data, including encryption in transit (SSL/TLS), access
        controls, least-privilege permissions, logging and monitoring, and
        vendor security reviews.
      </p>
      <p>
        <strong>User responsibility:</strong> keep your credentials confidential and notify us
        promptly as soon as you suspect any unauthorized access.
      </p>

      <h3 class="mt-4">
        7. DATA RETENTION
      </h3>
      <p>
        We retain personal data only as long as may be needed for the purposes
        described above, unless longer retention is required or permitted by
        law.
      </p>
      <ul>
        <li><strong>KYC/AML and transaction records:</strong> shall be typically retained for at least 5 years and up to 10 years after the end of the relationship (depending on the duly applicable UAE laws/regulatory requirements).</li>
        <li><strong>Account and platform logs:</strong> shall be retained for a limited period for security and troubleshooting purposes.</li>
        <li><strong>Marketing preferences:</strong> shall be retained until you opt out or we no longer have due basis to contact you.</li>
      </ul>

      <h3 class="mt-4">
        8. YOUR RIGHTS AND CHOICES
      </h3>
      <p>
        Subject to applicable UAE data protection laws (including, where
        applicable, DIFC or ADGM regimes), you may enjoy rights to: access,
        correct, delete or anonymize, restrict processing, object to certain
        processing, withdraw consent, and request portability where available.
      </p>
      <p>
        Certain requests may be limited (e.g., where we are supposed to retain
        records for AML/CTF).
      </p>
      <p>
        To opt out of marketing, use the unsubscribe link or contact us. You may
        also adjust cookie preferences where available.
      </p>

      <h3 class="mt-4">
        9. CONTACT AND UPDATES
      </h3>
      <p>
        Should you have any questions, requests or complaints, please contact
        our Data Privacy Team at <a href="mailto:privacy@rwa.capital">privacy@rwa.capital</a>
      </p>
      <p>
        This Policy may be updated from time to time; should this be the case,
        the updated version will be made available on the Platform with a
        revised effective date.
      </p>
    </div>
  </div>
</template>

<script setup>
import AuButton from "@/components/extended/AuButton.vue";
import { checkAccess, scope } from "@/help/accessData";
import { onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const openDoc = () => {
  window.open("/docs/Privacy_policy_rwa.pdf", "_blank");
};
onMounted(() => {
  if (checkAccess(scope.notRwa)) {
    router.push({ name: "page not found" });
  }
});
</script>

<style scoped lang="scss">
ul {
  margin-left: 1.5rem;
  margin-bottom: 1rem;

  li {
    margin-bottom: 0.5rem;
  }
}

.terms {
  background-color: var(--main-color);
  padding-bottom: 10px;

  .header {
    display: grid;
    grid-template-columns: 33% auto 33%;
    padding-right: 20px;
  }
}

a {
  color: var(--color-green);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>